import defaultShareList from '@/assets/share/defaultShareList.json';
import type {
  AnalysisShareContext,
  AnalysisShareOptions,
  BookConfigLike,
  BrowserShareType,
  BuildShareUrlOptions,
  DeviceInfo,
  JQueryLike,
  LogoPathConfig,
  MobileShareConfig,
  NativePlatform,
  NativeShareInfo,
  QqBrowserShareInfo,
  RawShareItem,
  ShareRuntimeWindow,
  ShareItem,
  SharePlatform,
  ShareState
} from '@/types/share';

const SHARE_URL_TEMPLATES: Record<SharePlatform, string> = {
  qqShare:
    'https://connect.qq.com/widget/shareqq/index.html?url=${url}&title=${title}&pics=${shot-img}',
  sinaweibo:
    'https://service.weibo.com/share/share.php?url=${url}&title=${title}&pic=${shot-img}&searchPic=true',
  baidutieba:
    'https://tieba.baidu.com/f/commit/share/openShareApi?url=${url}&text=${title}&pic=${shot-img}',
  qzone:
    'https://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url=${url}&title=${title}&pics=${shot-img}',
  linkedin:
    'https://www.linkedin.com/shareArticle?mini=true&ro=true&title=${title}&url=${url}&source=0&summary=${title}',
  douban: 'https://shuo.douban.com/!service/share?href=${url}&name=${title}&image=${shot-img}'
};

let qqBridgeLoaded = false;

/** 获取当前运行时的书籍配置。 */
function getBookConfig(overrideConfig?: BookConfigLike): BookConfigLike {
  return overrideConfig ?? getShareRuntimeWindow().bookConfig ?? {};
}

/** 获取分享旧运行时对象，避免扩展全局 Window 类型。 */
function getShareRuntimeWindow(): Window & ShareRuntimeWindow {
  return window as Window & ShareRuntimeWindow;
}

/** 获取 meta 标签内容，替代旧脚本里的 getMeta 全局函数。 */
function getMetaContent(name: string): string {
  const meta = document.querySelector<HTMLMetaElement>(
    `meta[property="${name}"], meta[name="${name}"]`
  );
  return meta?.content ?? '';
}

/** 从指定标记开始截断字符串，替代旧脚本里的 removeStartFrom 原型扩展。 */
function removeStartFrom(value: string, marker: string, replacement = ''): string {
  const index = value.indexOf(marker);
  if (index < 0) return value;
  return value.slice(0, index) + replacement;
}

/** 全量替换字符串，替代旧脚本里的 replaceAll 第三个参数写法。 */
function replaceAllText(value: string, searchValue: string, replaceValue: string): string {
  return value.split(searchValue).join(replaceValue);
}

/** 将相对地址转为浏览器可访问的绝对地址。 */
function getRealUrl(url: string): string {
  if (!url) return '';

  try {
    return new URL(url, window.location.href).href;
  } catch {
    return url;
  }
}

/** 读取当前页面 host，替代旧脚本里的 getHost 全局函数。 */
function getHost(url: string): string {
  try {
    return new URL(url, window.location.href).host;
  } catch {
    return window.location.host;
  }
}

/** 判断当前设备是否为手机。 */
function isPhone(): boolean {
  return /iphone|ipod|android.*mobile|windows phone/i.test(navigator.userAgent);
}

/** 判断当前设备是否为平板。 */
function isPad(): boolean {
  return /ipad|tablet|android(?!.*mobile)/i.test(navigator.userAgent);
}

/** 判断当前设备是否为 PC。 */
function isPC(): boolean {
  return !isPhone() && !isPad();
}

/** 安全调用页面上的 jQuery 风格工具。 */
function getJQuery(): ((selector: string | HTMLElement) => JQueryLike) | undefined {
  const maybeJquery = (window as unknown as { $?: (selector: string | HTMLElement) => JQueryLike })
    .$;
  return typeof maybeJquery === 'function' ? maybeJquery : undefined;
}

/** 获取 UC/QQ/微信环境信息。 */
function getDeviceInfo(): DeviceInfo {
  const ua = navigator.appVersion;
  let uc: 0 | 1 = ua.split('UCBrowser/').length > 1 ? 1 : 0;
  let qq: 0 | 1 | 2 = ua.split('MQQBrowser/').length > 1 ? 2 : 0;
  const wx =
    ua
      .match(/MicroMessenger/i)
      ?.toString()
      .toLowerCase() === 'micromessenger';
  const qqVersion = qq ? parseFloat(ua.split('MQQBrowser/')[1]) : 0;
  const ucVersion = uc ? parseFloat(ua.split('UCBrowser/')[1]) : 0;
  const userAgent = navigator.userAgent;
  const os: 0 | 1 | 2 = /iphone|ipod/i.test(userAgent) ? 1 : /android/i.test(userAgent) ? 2 : 0;

  // 步骤 1：过滤不支持分享桥接的旧版 QQ 浏览器。
  if ((qq && qqVersion < 5.4 && os === 1) || (qq && qqVersion < 5.3 && os === 1)) {
    qq = 0;
  } else if (qq && qqVersion < 5.4 && os === 2) {
    qq = 1;
  } else if (uc && ((ucVersion < 10.2 && os === 1) || (ucVersion < 9.7 && os === 2))) {
    uc = 0;
  }

  return { os, qq, uc, wx };
}

/** 将分享类型转为 UC 浏览器平台名称。 */
function getUcPlatform(type: BrowserShareType | undefined, os: DeviceInfo['os']): NativePlatform {
  if (typeof type === 'undefined') return '';

  if (os === 2) {
    if (type === 1) return 'WechatTimeline';
    if (type === 2) return 'WechatFriends';
    if (type === 3) return 'QQ';
    if (type === 4) return 'SinaWeibo';
  }

  if (os === 1) {
    if (type === 1) return 'kWeixinFriend';
    if (type === 2) return 'kWeixin';
    if (type === 3) return 'kQQ';
    if (type === 4) return 'kSinaWeibo';
  }

  return type === 0 ? 'undefined' : '';
}

/** 将分享类型转为 QQ 浏览器平台编号。 */
function getQqPlatform(type: BrowserShareType | undefined): number | 'undefined' | '' {
  if (type === 1) return 8;
  if (type === 2) return 1;
  if (type === 3) return 4;
  if (type === 4) return 11;
  if (type === 0) return 'undefined';
  return '';
}

/** 加载 QQ 浏览器分享桥接脚本。 */
function loadQqApi(qq: DeviceInfo['qq'], callback?: () => void): void {
  if (!qq) {
    callback?.();
    return;
  }

  const script = document.createElement('script');
  script.onload = () => callback?.();
  script.onerror = () => {};
  script.src = qq === 1 ? '//3gimg.qq.com/html5/js/qb.js' : '//jsapi.qq.com/get?api=app.share';
  document.body.appendChild(script);
}

/** 使用 UC 浏览器原生分享能力。 */
function ucShare(config: MobileShareConfig, deviceInfo: DeviceInfo): void {
  const platform = getUcPlatform(config.type, deviceInfo.os);
  const shareInfo: NativeShareInfo = [config.title, config.desc, config.url, platform, '', '', ''];

  const runtimeWindow = getShareRuntimeWindow();

  if (runtimeWindow.ucweb?.startRequest) {
    runtimeWindow.ucweb.startRequest('shell.page_share', shareInfo);
    return;
  }

  runtimeWindow.ucbrowser?.web_share?.apply(null, shareInfo);
}

/** 使用 QQ 浏览器原生分享能力。 */
function qqShare(config: MobileShareConfig, deviceInfo: DeviceInfo): void {
  const share = () => {
    const shareInfo: QqBrowserShareInfo = {
      url: config.url,
      title: config.title,
      description: config.desc,
      img_url: config.img,
      img_title: config.title,
      to_app: getQqPlatform(config.type),
      cus_txt: ''
    };

    const runtimeWindow = getShareRuntimeWindow();
    if (runtimeWindow.browser?.app?.share) {
      runtimeWindow.browser.app.share(shareInfo);
    } else if (runtimeWindow.qb?.share) {
      runtimeWindow.qb.share(shareInfo);
    }
  };

  if (qqBridgeLoaded) {
    share();
    return;
  }

  loadQqApi(deviceInfo.qq, () => {
    qqBridgeLoaded = true;
    share();
  });
}

/** 创建当前页面默认分享状态。 */
export function createShareState(options: AnalysisShareOptions = {}): ShareState {
  const config = getBookConfig(options.bookConfig);
  const pageUrl = removeStartFrom(window.location.href, '#p=');
  const shareUrl = options.url ?? config.socialShareLink ?? pageUrl;
  const shareTitle = options.title ?? `${window.document.title}%20`;
  const description =
    options.description ??
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.content ??
    '';
  const thumbUrl = options.screenshot ?? getAnalysisThumbUrl();
  const baseUrl = removeStartFrom(pageUrl, '/mobile');
  const basePath = baseUrl.substring(0, baseUrl.lastIndexOf('/') + 1);
  const facebookScreenshot = config.facebookShotImageURL
    ? `${basePath}${config.facebookShotImageURL}`
    : thumbUrl;

  return {
    url: shareUrl,
    title: shareTitle,
    description,
    screenshot: getRealUrl(thumbUrl),
    facebookScreenshot: getRealUrl(facebookScreenshot)
  };
}

/** 获取默认分享缩略图地址。 */
export function getAnalysisThumbUrl(): string {
  const url = getMetaContent('og:image');
  return url.includes('../') ? url.replace('../', '/') : url;
}

/** 构建指定平台的分享跳转地址。 */
export function buildPlatformShareUrl(
  platform: SharePlatform,
  options: BuildShareUrlOptions = {}
): string {
  const state = createShareState(options);
  const screenshot =
    platform === 'baidutieba' ? state.screenshot.replace('https', 'http') : state.screenshot;
  const url = platform === 'sinaweibo' ? getShotImage(options.url) : screenshot;

  return replaceShareVariables(SHARE_URL_TEMPLATES[platform], {
    ...state,
    screenshot: url
  });
}

/** 打开指定平台的分享窗口。 */
export function openPlatformShare(
  platform: SharePlatform,
  options: BuildShareUrlOptions = {}
): void {
  window.open(buildPlatformShareUrl(platform, options), '_blank', 'noopener,noreferrer');
}

/** 替换分享 URL 模板中的变量。 */
export function replaceShareVariables(templateUrl: string, state = createShareState()): string {
  return templateUrl
    .replace(/\$\{url\}/g, encodeURIComponent(state.url))
    .replace(/\$\{title\}/g, encodeURIComponent(state.title))
    .replace(/\$\{shot-img\}/g, encodeURIComponent(state.screenshot))
    .replace(/\$\{description\}/g, encodeURIComponent(state.description))
    .replace(/share_url/g, JSON.stringify(state.url))
    .replace(/share_title/g, JSON.stringify(state.title))
    .replace(/share_description/g, JSON.stringify(state.description))
    .replace(/share_screenshot/g, JSON.stringify(state.screenshot))
    .replace(/facebook_screenshot/g, JSON.stringify(state.facebookScreenshot));
}

/** 获取分享截图地址。 */
export function getShotImage(shareUrl?: string): string {
  const config = getBookConfig();
  const url = config.socialShareLink ?? shareUrl ?? window.location.href;
  return `${url.replace('/index.html', '').replace('/mobile', '').replace('/articles', '')}/shot.jpg`;
}

/** 生成邮件分享链接。 */
export function getEmailUrl(gmailUrl?: string, state = createShareState()): string {
  const config = getBookConfig();

  if (!config.btnShareWithEmailSubject && !config.btnShareWithEmailBody && gmailUrl) {
    return gmailUrl
      .replace(/\$\{url\}/g, encodeURIComponent(state.url))
      .replace(/\$\{title\}/g, encodeURIComponent(state.title));
  }

  const emailTo = config.btnShareWithEmailTo ? encodeURIComponent(config.btnShareWithEmailTo) : '';
  const emailTitle = encodeURIComponent(config.btnShareWithEmailSubject || state.title);
  const emailDescription = encodeURIComponent(
    replaceAllText(
      replaceAllText(
        replaceAllText(config.btnShareWithEmailBody || state.description, '{link}', state.url),
        '{thumb}',
        state.screenshot
      ),
      '{title}',
      state.title
    )
  );

  return `mailto:${emailTo}?subject=${emailTitle}&body=${emailDescription}`;
}

/** 展示二维码面板。 */
export function openQRCode(): void {
  getShareRuntimeWindow().sharePanel?.showQRCode?.();
}

/** 展示旧版图片弹窗。 */
export function openPopUp(url: string): void {
  const $ = getJQuery();

  if (!$) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  const background = $(
    "<div style='position:absolute;left:0;top:0;width:100%;height:100%;background:rgba(0,0,0,0.6);z-index:1000;'></div>"
  );
  const image = $(
    "<img alt='' style='position:absolute;top:0;right:0;max-width:90%;max-height:90%;' src='" +
      url +
      "'/>"
  );
  background.append(image);
  $('body').append(background);
  const removeBackground = () => {
    background.hide();
    background.remove();
  };

  if (background.onClick) {
    background.onClick(removeBackground);
  } else {
    background.bind('click', removeBackground);
  }
}

/** 分享到 Facebook。 */
export function shareToFacebook(state = createShareState()): void {
  const config = getBookConfig();
  const runtimeWindow = getShareRuntimeWindow();
  const isFlipWebsite = () => {
    const host = getHost(window.location.href).toLowerCase();
    return (
      host.includes('fliphtml5.com') ||
      host.includes('pubhtml5.com') ||
      host.includes('anyflip.com') ||
      host.includes('flipbuilder.com') ||
      Boolean(config.facebookAppId)
    );
  };
  const getAppId = () => {
    const host = getHost(window.location.href).toLowerCase();
    if (host.includes('fliphtml5.com')) return '759223277462081';
    if (host.includes('pubhtml5.com')) return '552959651503135';
    if (host.includes('anyflip.com')) return '926249944075860';
    if (host.includes('flipbuilder.com')) return '1836674576427110';
    return config.facebookAppId;
  };

  // 步骤 1：站点支持 FB SDK 时优先使用 SDK。
  if (isFlipWebsite() && runtimeWindow.FB) {
    runtimeWindow.FB.init({
      appId: getAppId(),
      status: true,
      xfbml: false,
      version: 'v2.4'
    });
    runtimeWindow.FB.ui({ method: 'share', href: state.url.toString() }, () => {});
    return;
  }

  // 步骤 2：没有 SDK 时使用普通分享地址兜底。
  const facebookUrl = state.facebookUrl ?? state.url;
  window.open(
    facebookUrl
      .replace('${url}', state.url)
      .replace('${shot-img}', state.facebookScreenshot)
      .replace('${description}', state.description),
    '_blank',
    'noopener,noreferrer'
  );
}

/** 手机端 QQ 分享。 */
export function sharePhoneQQ(state = createShareState()): void {
  const ua = navigator.appVersion;
  const uc = ua.split('UCBrowser/').length > 1;
  const qq = ua.split('MQQBrowser/').length > 1;
  const isQq = ua.toLowerCase().match(/ qq/i) || ua.toLowerCase().match(/ qqtheme/i) !== null;
  const wx =
    ua
      .match(/MicroMessenger/i)
      ?.toString()
      .toLowerCase() === 'micromessenger';

  if ((uc || (qq && !wx)) && !isQq) {
    initMobileShare(
      {
        title: state.title,
        url: state.url,
        desc: state.description,
        img: state.screenshot
      },
      3
    );
    return;
  }

  showMobileHelpLayer(getShareRuntimeWindow().phoneSvgIcons?.shareHelpSvg);
}

/** 手机端微信分享引导。 */
export function sharePhoneWeChat(): void {
  showMobileHelpLayer(getShareRuntimeWindow().phoneSvgIcons?.shareToWeChat);
}

/** 展示海报面板。 */
export function showPoster(onShowPoster: () => void = () => {}): void {
  const runtimeWindow = getShareRuntimeWindow();
  if (!runtimeWindow.sharePoster && runtimeWindow.SharePoster) {
    runtimeWindow.sharePoster = new runtimeWindow.SharePoster(runtimeWindow.tmpContainer);
  }
  runtimeWindow.sharePoster?.show();
  onShowPoster();
}

/** 展示移动端分享引导层。 */
function showMobileHelpLayer(backgroundUrl?: string): void {
  if (!backgroundUrl) return;

  const overlay = document.createElement('div');
  overlay.style.cssText =
    'text-align:center;position:fixed;top:0;left:0;right:0;bottom:0;z-index:2200;width:100%;height:100%;background:rgba(0,0,0,0.85);';

  const helpBg = document.createElement('div');
  helpBg.style.cssText =
    'width:92%;height:96%;margin:2% 4%;background:url(' +
    backgroundUrl +
    ') center top no-repeat;background-size:contain;';

  overlay.appendChild(helpBg);
  overlay.addEventListener('click', () => overlay.remove(), { once: true });
  document.body.appendChild(overlay);
}

/** 根据当前浏览器环境执行移动端分享回调。 */
export function checkMobileShare(
  successFn: () => void,
  wxFn: () => void,
  failFn: () => void
): void {
  const deviceInfo = getDeviceInfo();
  if (deviceInfo.uc || (deviceInfo.qq && !deviceInfo.wx)) {
    successFn();
  } else if (deviceInfo.wx) {
    wxFn();
  } else {
    failFn();
  }
}

/** 初始化并触发移动端原生分享。 */
export function initMobileShare(config: MobileShareConfig, type?: BrowserShareType): void {
  const shareConfig = { ...config };
  if (typeof type !== 'undefined') shareConfig.type = type;

  try {
    const deviceInfo = getDeviceInfo();
    if (deviceInfo.uc) {
      ucShare(shareConfig, deviceInfo);
    } else if (deviceInfo.qq && !deviceInfo.wx) {
      qqShare(shareConfig, deviceInfo);
    }
  } catch {
    // 移动端原生分享失败时保持旧逻辑：静默失败，不影响页面继续操作。
  }
}

/** 创建 AnalysisShare 函数式上下文。 */
export function createAnalysisShareContext(
  options: AnalysisShareOptions = {}
): AnalysisShareContext {
  return {
    bookConfig: getBookConfig(options.bookConfig),
    loadingPromise: null,
    shareList: [],
    state: createShareState(options)
  };
}

/** 初始化并刷新分享上下文。 */
export async function initializeAnalysisShareContext(context: AnalysisShareContext): Promise<void> {
  // 步骤 1：重新计算当前页面分享状态。
  context.state = createShareState({ bookConfig: context.bookConfig });

  // 步骤 2：按配置加载分享列表。
  await refreshAnalysisShareList(context);
}

/** 获取带缓存的分享列表。 */
export async function getAnalysisShareList(context: AnalysisShareContext): Promise<ShareItem[]> {
  if (context.shareList.length > 0) return context.shareList;
  if (context.loadingPromise) return await context.loadingPromise;

  context.loadingPromise = loadAnalysisShareList(context);

  try {
    context.shareList = await context.loadingPromise;
    return context.shareList;
  } finally {
    context.loadingPromise = null;
  }
}

/** 根据配置类型加载默认或区域分享列表。 */
async function loadAnalysisShareList(context: AnalysisShareContext): Promise<ShareItem[]> {
  return String(context.bookConfig.socialShareType) === '1'
    ? await getRegionShareList(context)
    : await getDefaultShareList(context);
}

/** 刷新分享列表中的变量。 */
export async function refreshAnalysisShareList(context: AnalysisShareContext): Promise<void> {
  if (context.shareList.length <= 0) {
    context.shareList = await getAnalysisShareList(context);
  }

  context.shareList = updateShareListVariables(context, context.shareList);
}

/** 替换分享列表每一项中的变量。 */
export function updateShareListVariables(
  context: AnalysisShareContext,
  shareList: ShareItem[]
): ShareItem[] {
  return shareList.map((item) => ({
    ...item,
    url: item.url ? replaceAnalysisShareUrlVariables(context, item.url) : item.url
  }));
}

/** 替换分享 URL 中的动态变量。 */
export function replaceAnalysisShareUrlVariables(
  context: AnalysisShareContext,
  url: string
): string {
  return replaceShareVariables(url, context.state);
}

/** 初始化并过滤分享列表。 */
export function initShareList(
  context: AnalysisShareContext,
  rawShareItems: RawShareItem[]
): ShareItem[] {
  if (!rawShareItems || rawShareItems.length <= 0) return [];

  return rawShareItems.reduce<ShareItem[]>((list, item) => {
    if (item.filter === 'mobile' && !isPC()) return list;
    if (item.filter === 'pc' && isPC() && !item.specail) return list;

    const info = fixShareItemInfo(context, item);
    if (info) list.push(info);
    return list;
  }, []);
}

/** 修正单个分享项信息。 */
export function fixShareItemInfo(
  context: AnalysisShareContext,
  item: RawShareItem
): ShareItem | undefined {
  if (!item) return undefined;

  // 步骤 1：替换分享项 URL 中的页面变量。
  let shareUrl = encodeURIComponent(context.state.url);
  let parentUrl = '';
  let topUrl = '';
  try {
    parentUrl = window.parent.location.href;
    topUrl = window.top?.location.href ?? '';
  } catch {
    // 跨域 iframe 无法读取父窗口地址时，保留空字符串作为替代值。
  }

  let url = item.url ?? '';
  if (url.includes('${url_no_page}')) {
    url = replaceAllText(url, '${url_no_page}', '');
    shareUrl = removeStartFrom(shareUrl, '#p=');
    parentUrl = removeStartFrom(parentUrl, '#p=');
    topUrl = removeStartFrom(topUrl, '#p=');
  }

  url = replaceAllText(url, '${url}', shareUrl);
  url = replaceAllText(url, '${url_no_page}', shareUrl);
  url = replaceAllText(url, '${url_parent}', parentUrl);
  url = replaceAllText(url, '${url_top}', topUrl);

  // 步骤 2：按平台处理截图和标题。
  if (item.type === 'baidutieba') {
    url = replaceAllText(url, '${shot-img}', context.state.screenshot.replace('https', 'http'));
  } else if (item.type === 'sinaweibo') {
    url = replaceAllText(url, '${shot-img}', getShotImage(context.state.url));
  } else {
    url = replaceAllText(url, '${shot-img}', context.state.screenshot);
  }
  url = replaceAllText(url, '${title}', window.document.title);

  if (!context.state.description) {
    url = url.replace('&desc=${description}', '').replace('&text=${description}', '');
  }

  // 步骤 3：把旧 javascript 字符串迁移成可识别动作。
  const logo =
    context.bookConfig.isFlipPdf && item.logo.startsWith('./') ? `.${item.logo}` : item.logo;
  const title = item.title;

  if (item.name?.toLowerCase() === 'facebook') {
    context.state.facebookUrl = item.facebookUrl;
    return { logo, url: '', title, type: item.type, action: 'facebook' };
  }

  if (item.type === 'qrcode') {
    return { url: '', logo, title, type: item.type, filter: item.filter, action: 'qrcode' };
  }

  if (item.popUp) {
    return { url: '', logo, title, type: item.type, action: 'popup', actionPayload: item.popUp };
  }

  if (item.type === 'qqShare' && (isPhone() || isPad())) {
    return { url: '', logo, title, type: item.type, action: 'phoneQQ' };
  }

  if (item.type === 'poster') {
    return { url: '', logo, title, type: item.type, action: 'poster' };
  }

  if (item.type === 'copy') {
    return { url: '', type: 'copy', logo, title };
  }

  return { url, logo, title, type: item.type };
}

/** 获取默认分享列表。 */
export async function getDefaultShareList(context: AnalysisShareContext): Promise<ShareItem[]> {
  try {
    const runtimeWindow = getShareRuntimeWindow();
    if (runtimeWindow.shareObj && runtimeWindow.shareObj.length > 0) {
      const filtered = filterAndSortShareList(context, runtimeWindow.shareObj);
      return initShareList(context, filtered);
    }

    const processedList = processShareList(context, defaultShareList as RawShareItem[]);
    const filteredList = filterAndSortShareList(context, processedList);

    return initShareList(context, filteredList);
  } catch (error) {
    console.error('Failed to load default share list:', error);
    return [];
  }
}

/** 处理默认分享列表的图标和 URL。 */
export function processShareList(
  context: AnalysisShareContext,
  shareList: RawShareItem[]
): RawShareItem[] {
  const { path, suffix } = getLogoPathConfig();

  return shareList.map((item) => ({
    ...item,
    logo: path + item.logo.replace('.svg', suffix),
    url: item.url ? replaceAnalysisShareUrlVariables(context, item.url) : item.url
  }));
}

/** 获取分享图标路径配置。 */
export function getLogoPathConfig(): LogoPathConfig {
  const runtimeWindow = getShareRuntimeWindow();
  let path = runtimeWindow.uiBaseURL ?? '';
  let suffix = '.svg';

  if (isPhone() || isPad()) {
    try {
      path = runtimeWindow.htmlConfig?.phoneTemplate?.path ?? runtimeWindow.phoneBaseURL ?? path;
      suffix = '_phone.svg';
    } catch (error) {
      console.warn('Failed to get phone logo path:', error);
    }
  }

  return { path, suffix };
}

/** 获取区域分享列表。 */
export async function getRegionShareList(context: AnalysisShareContext): Promise<ShareItem[]> {
  try {
    const path = getRealPath(context);
    if (!path) return [];

    const filePath = `${path}/share/regionShareList.json`;
    const fallbackPath = './files/share/regionShareList.json';
    const regionList = await readJsonFile(filePath, fallbackPath);
    const processedList = processRegionList(context, regionList);
    const filteredList = filterAndSortShareList(context, processedList);

    return initShareList(context, filteredList);
  } catch (error) {
    console.error('Failed to load region share list:', error);
    return [];
  }
}

/** 处理区域分享列表的图标和 URL。 */
export function processRegionList(
  context: AnalysisShareContext,
  regionList: RawShareItem[]
): RawShareItem[] {
  return regionList.map((item) => ({
    ...item,
    logo: `${getShareRuntimeWindow().uiBaseURL ?? ''}${item.logo}`,
    url: processRegionUrl(context, item)
  }));
}

/** 处理区域分享项 URL。 */
export function processRegionUrl(context: AnalysisShareContext, item: RawShareItem): string {
  if (item.url === 'EMAIL_PLACEHOLDER') {
    return getEmailUrl(item.gmailUrl, context.state);
  }

  return replaceAnalysisShareUrlVariables(context, item.url ?? '');
}

/** 按配置过滤并排序分享列表。 */
export function filterAndSortShareList(
  context: AnalysisShareContext,
  shareList: RawShareItem[]
): RawShareItem[] {
  let socialSharingList = context.bookConfig.socialSharingList;

  if (
    socialSharingList === '' ||
    (typeof socialSharingList === 'string' && socialSharingList.trim() === '')
  ) {
    return shareList;
  }

  if (typeof socialSharingList === 'string') {
    const configText = socialSharingList.trim();
    try {
      socialSharingList = JSON.parse(configText) as string[];
    } catch {
      socialSharingList = configText
        .split(/[,;]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  if (!socialSharingList || !Array.isArray(socialSharingList)) {
    return shareList;
  }

  if (socialSharingList.length === 0) {
    context.bookConfig.SmallProgramUrl = '';
    return [];
  }

  const socialSharingMap = socialSharingList.reduce<Record<string, number>>((map, type, index) => {
    map[type] = index;
    return map;
  }, {});

  const filteredList = shareList.filter((item) => {
    if (item.type === 'qrcode') {
      return (
        Object.prototype.hasOwnProperty.call(socialSharingMap, item.type) ||
        Object.prototype.hasOwnProperty.call(socialSharingMap, 'miniCode')
      );
    }

    return Object.prototype.hasOwnProperty.call(socialSharingMap, item.type);
  });

  filteredList.sort((a, b) => {
    const indexA = socialSharingMap[a.type] ?? socialSharingMap.miniCode ?? Number.MAX_SAFE_INTEGER;
    const indexB = socialSharingMap[b.type] ?? socialSharingMap.miniCode ?? Number.MAX_SAFE_INTEGER;
    return indexA - indexB;
  });

  if (!socialSharingList.includes('miniCode')) {
    context.bookConfig.SmallProgramUrl = '';
  }

  return filteredList;
}

/** 获取分享配置文件真实路径。 */
export function getRealPath(context: AnalysisShareContext): string {
  if (context.bookConfig.UIBaseURL) {
    return `${new URL(context.bookConfig.UIBaseURL).origin}/resourceFiles`;
  }

  return './';
}

/** 读取 JSON 文件，主路径失败后尝试备用路径。 */
export async function readJsonFile(
  filePath: string,
  fallbackPath: string | null = null
): Promise<RawShareItem[]> {
  try {
    const response = await fetch(filePath);
    if (!response.ok) {
      console.warn('Failed to fetch JSON file:', filePath);
    }

    return (await response.json()) as RawShareItem[];
  } catch (fetchError) {
    console.warn('Primary path failed:', filePath, fetchError);

    if (fallbackPath) {
      try {
        const response = await fetch(fallbackPath);
        if (!response.ok) {
          console.warn('Failed to fetch JSON file:', fallbackPath);
          return [];
        }

        return (await response.json()) as RawShareItem[];
      } catch (fallbackError) {
        console.error('Fallback path also failed:', fallbackPath, fallbackError);
        return [];
      }
    }

    return [];
  }
}
